
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import axiosInstance from "../../../axiosSetup";
import { encryptAndEncode } from "../../../encrypt-util";
import { RootState } from "../template/store";

export interface SezApiError {
  timestamp?: string;
  status: number;
  error: string;
  message: string;
}

const getSezErrorMessage = (
  error: AxiosError<any> | undefined,
  fallback: string
): string => {
  const data = error?.response?.data;
  const message =
    data?.message ||
    (typeof data === "string" && data.trim() ? data : null) ||
    error?.message;
  if (
    typeof message === "string" &&
    message.trim() &&
    !message.toLowerCase().includes("request failed") &&
    !message.toLowerCase().includes("rejected")
  ) {
    return message;
  }
  return fallback;
};


const toSezApiError = (
  e: unknown,
  fallbackMessage: string,
  fallbackError = "SEZ_API_FAILED"
): SezApiError => {
  const maybe = e as { data?: SezApiError };
  if (maybe?.data?.message && typeof maybe.data.status === "number") {
    return maybe.data;
  }

  const error = e as AxiosError<any>;
  const errorData = error?.response?.data;
  return {
    timestamp: errorData?.timestamp,
    status: errorData?.status || error?.response?.status || 500,
    error: errorData?.error || fallbackError,
    message: getSezErrorMessage(error, fallbackMessage),
  };
};


const assertSezOk = <T>(
  response: AxiosResponse<T>,
  fallbackMessage: string
): T => {
  if (response.status !== 200 || response.data == null) {
    const err: SezApiError = {
      status: response.status,
      error: "SEZ_API_FAILED",
      message:
        (response.data as any)?.message ||
        `${fallbackMessage} (HTTP ${response.status})`,
    };
    throw Object.assign(new Error(err.message), { data: err, response });
  }
  return response.data;
};


export interface AnalyticsDashboardResponse {
  status: string;
  data_source: string;
  filters: {
    date_range: string;
    partner_id: number | null;
    start_date: string;
    end_date: string;
  };
  kpis: {
    total: number;
    pending: number;
    submitted: number;
    approved: number;
    rejected: number;
    auto_approved: number;
    manually_approved: number;
  };
  charts: {
    status_donut: { labels: string[]; data: number[] };
    monthly_trend: {
      labels: string[];
      pending: number[];
      submitted: number[];
      approved: number[];
      rejected: number[];
    };
    aging_buckets: { labels: string[]; data: number[] };
    approval_mix: { labels: string[]; data: number[] };
    mismatch_by_field: { labels: string[]; data: number[] };
  };
  tables: {
    top_payors?: { title?: string; rows?: any[] };
    certificate_not_matched?: { title?: string; rows?: any[] };
    [title: string]: { title?: string; rows?: any[] } | any[] | undefined;
  };
  tables_pagination?: Record<
    string,
    {
      pagination_enabled: boolean;
      page: number;
      page_size: number;
      total_records: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    }
  >;
}

export interface SezDashboardRequest {
  dateRange: string;
  topPayorsPage: number;
  topPayorsPageSize: number;
  certificatesPage: number;
  certificatesPageSize: number;
}

export const fetchSezDashboard = async (
  request: SezDashboardRequest
) => {
  const response = await axiosInstance.get<AnalyticsDashboardResponse>(
    `/api/edo-rest-v2/v1/sez/sez-dashboard`,
    {
      params: {
        date_range: request.dateRange,
        top_payors_page: request.topPayorsPage,
        top_payors_page_size: request.topPayorsPageSize,
        certificates_page: request.certificatesPage,
        certificates_page_size: request.certificatesPageSize,
      },
    }
  );

  return assertSezOk(response, "Failed to fetch SEZ dashboard analytics");
};

export const exportSezDashboard = (
  dateRange: string,
  exportType: "certificate" | "toppayors"
) =>
  axiosInstance.get(
    `/api/edo-rest-v2/v1/sez/sez-dashboard/export`,
    {
      params: {
        date_range: dateRange,
        top_payors_page: 1,
        top_payors_page_size: exportType === "toppayors" ? 50 : 5,
        certificates_page: 1,
        certificates_page_size: exportType === "certificate" ? 2 : 10,
        export: exportType,
      },
      responseType: "arraybuffer",
    }
  );

export const getSezDashboard = createAsyncThunk(
  "sezDashboard/getSezDashboard",
  async (request: SezDashboardRequest, { rejectWithValue }) => {
    try {
      return await fetchSezDashboard(request);
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(e, "Failed to fetch SEZ dashboard analytics", "SEZ_DASHBOARD_FAILED")
      );
    }
  }
);

export const getSezRejectedMessage = (
  e: unknown,
  fallback: string
): string => {
  const payload = e as SezApiError & { message?: string };
  if (typeof payload?.message === "string" && payload.message.trim()) {
    const msg = payload.message.trim();
    if (
      !msg.toLowerCase().includes("request failed") &&
      !msg.toLowerCase().includes("rejected")
    ) {
      return msg;
    }
  }
  return fallback;
};

export interface IUploadSezCertificatePayload {
  fileName: string;
  serverFileName: string;
  serverFilePath: string;
  status: string;
}

export interface IUploadSezCertificateResult {
  uploadId: number;
  fileName: string;
  filePath: string;
  totalRows: number;
  insertedRows: number;
  failedRows: number;
  status: string;
  message: string;
  errorFileName?: string;
  errorFilePath?: string;
  errorFileUrl?: string;
  autoApprovedRows?: number;
  mismatchedRows?: number;
}

export interface ISezCertificateUploadPayload {
  fileName: string;
  serverFileName: string;
  serverFilePath: string;
  sezCertReqId: number;
  loginUser: string;
  locationId: number;
}

export interface ISezCertificateUploadInvoice {
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  amountInr?: string | null;
  invoiceType?: string | null;
  sezOnlineReferenceNumber?: string | null;
  sezCertReqId?: number | null;
  status?: string | null;
  blNo?: string | null;
  payorId?: number | null;
  consigneeName?: string | null;
  invoiceReleasedTo?: string | null;
  remarks?: string | null;
  issueDate?: string | null;
  sezInvNo?: string | null;
  sezInvDt?: string | null;
  sezInvAmt?: string | null;
  sezInvTp?: string | null;
}

export interface ISezCertificateUploadResult {
  status?: string;
  message?: string;
  sezCertReqId?: number;
  requestStatus?: string;
  attachmentUrl?: string;
  requestId?: string;
  invoices?: ISezCertificateUploadInvoice[];
  [key: string]: any;
}

export interface ISezCertificateFinalSubmitInvoice {
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  amountInr?: string | null;
  invoiceType?: string | null;
  sezOnlineReferenceNumber?: string | null;
  sezCertReqId?: number | null;
  status?: string | null;
  blNo?: string | null;
  payorId?: number | null;
  consigneeName?: string | null;
  invoiceReleasedTo?: string | null;
  remarks?: string | null;
  issueDate?: string | null;
  sezInvNo?: string | null;
  sezInvDt?: string | null;
  sezInvAmt?: string | null;
  sezInvTp?: string | null;
}


export interface ISezCertificateFinalSubmitPayload {
  sezCertReqId: number;
  requestId: string;
  issueDate: string;
  requestStatus?: string;
  invoices: ISezCertificateFinalSubmitInvoice[];
}

export interface ISezCertificateFinalSubmitResult {
  status?: string;
  message?: string;
  sezCertReqId?: number;
  requestStatus?: string;
  attachmentUrl?: string;
  requestId?: string;
  invoices?: ISezCertificateFinalSubmitInvoice[];
  [key: string]: any;
}


export interface SezApproveResult {

    status: string;
    message: string;
    errorCode: string;

}



export interface SezApproveRequest {
  odexPartnerId: number;
  sezCertReqId: number;
  status: string;    
  email: string;
  remarks?: string;
  requestId: string;
  issueDate: string;
}

export interface SezCertificateFieldDifference {
  fieldName: string;
  invoiceValue: string | null;
  certificateValue: string | null;
  status: string;
}

export interface SezCertificateFieldDifferencesResult {
  status: string;
  message: string;
  sezCertReqId: number;
  overallResult: string;
  matchedFields: number;
  mismatchedFields: number;
  blNo?: string | null;
  invoiceNo?: string | null;
  partnerName?: string | null;
  comparison: SezCertificateFieldDifference[];
}

export const uploadSezCertificateExcel = createAsyncThunk(
  "sezDashboard/uploadSezCertificateExcel",
  async (payload: IUploadSezCertificatePayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<IUploadSezCertificateResult>(
        `/api/edo-rest-v2/v1/sez/uploadSezCertificateExcel`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return assertSezOk(response, "Failed to upload SEZ Certificate");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(e, "Failed to upload SEZ Certificate", "EXCEL_UPLOAD_FAILED")
      );
    }
  }
);

export const getInvoiceUploadHistory = createAsyncThunk(
  "sezDashboard/getInvoiceUploadHistory",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const createdBy = state.userProfile?.profile?.email || "";

      const response = await axiosInstance.get(
        `/api/edo-rest-v2/v1/sez/invoiceUploadHistory`,
        {
          params: { createdBy },
        }
      );

      return assertSezOk(response, "Failed to fetch upload history");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(e, "Failed to fetch upload history", "UPLOAD_HISTORY_FAILED")
      );
    }
  }
);

export const uploadSezCertificate = createAsyncThunk(
  "sezDashboard/uploadSezCertificate",
  async (payload: ISezCertificateUploadPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<ISezCertificateUploadResult>(
        `/api/edo-rest-v2/v1/sez/sezCertificateUpload`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return assertSezOk(response, "Certificate upload failed");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(e, "Failed to upload SEZ Certificate", "CERTIFICATE_UPLOAD_FAILED")
      );
    }
  }
);


export const submitSezCertificateFinal = createAsyncThunk(
  "sezDashboard/submitSezCertificateFinal",
  async (payload: ISezCertificateFinalSubmitPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<ISezCertificateFinalSubmitResult>(
        `/api/edo-rest-v2/v1/sez/submitSezCertificateDetails`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return assertSezOk(response, "Failed to submit SEZ certificate details");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(
          e,
          "Failed to submit certificate details",
          "CERTIFICATE_SUBMIT_FAILED"
        )
      );
    }
  }
);


export const ApproveSezCertificate = createAsyncThunk(
  "sezDashboard/sezApproveReject",
  async (payload: SezApproveRequest, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<SezApproveResult>(
        `/api/edo-rest-v2/v1/sez/sezApproveReject`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return assertSezOk(response, "Failed to approve/reject certificate");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(e, "Failed to approve/reject request", "SEZ_APPROVE_REJECT_FAILED")
      );
    }
  }
);

export const getSezOpenDocumentUrl = (
  id: string | number,
  linerId: string | number
) =>
  `/api/edo-rest-v2/v1/sez/openDocument?id=${encryptAndEncode(id)}&moduleTp=SEZ&linerId=${encryptAndEncode(linerId)}&rd=N`;

export interface SezCertificateRequestItem {
  sezCertReqId: number;
  linerId?: number | null;
  partenerId?: number | null;
  invInfoId?: number | null;
  payorId?: number | null;
  blNo?: string | null;
  invNo?: string | null;
  invDt?: string | null;
  invAmt?: number | null;
  invTp?: string | null;
  consigneeNm?: string | null;
  invoiceReleasedTo?: string | null;
  sezRequestId?: string | number | null;
  requesterId?: string | number | null;
  sezCertIssueDt?: string | null;
  status?: string | null;
  remarks?: string | null;
  customerRemarks?: string | null;
  isManuallyClosed?: boolean;
  invCurrency?: string | null;
  liner?: string | null;
  sezInvNo?: string | null;
  sezInvDt?: string | null;
  sezInvAmt?: number | null;
  sezInvTp?: string | null;
  countryCode?: string | null;
  approvedBy?: string | null;
  approvedDt?: string | null;
  createdBy?: string | null;
  createdOn?: string | null;
  modifiedBy?: string | null;
  modifiedOn?: string | null;
  pending_since?: number | string | null;
}

export interface SezCertificateRequestResult {
  status: string;
  message: string;
  totalRecords: number;
  data: SezCertificateRequestItem[];
}

export const getSezCertificateRequest = createAsyncThunk(
  "sezDashboard/getSezCertificateRequest",
  async (sezCertReqId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<SezCertificateRequestResult>(
        `api/edo-rest-v2/v1/sez/getSezCertificateRequest`,
        {
          params: { sezCertReqId },
        }
      );

      return assertSezOk(response, "Failed to fetch SEZ certificate request details");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(
          e,
          "Failed to fetch invoice details",
          "SEZ_CERTIFICATE_REQUEST_FAILED"
        )
      );
    }
  }
);

export interface SezCertReqHistoryItem {
  status: string;
  prevStatus?: string;
  createdBy: string;
  requesterId?: string;
  timeLine: string;
  remarks?: string;
  actionType?: string;
}

export interface SezCertReqHistoryResult {
  status: string;
  message: string;
  sezCertReqId: number;
  history: SezCertReqHistoryItem[];
}

export const getSezCertReqHistory = createAsyncThunk(
  "sezDashboard/getSezCertReqHistory",
  async (sezCertReqId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<SezCertReqHistoryResult>(
        `/api/edo-rest-v2/v1/sez/sezCertReqHistory`,
        {
          params: { sezCertReqId },
        }
      );

      return assertSezOk(response, "Failed to fetch SEZ certificate request history");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(
          e,
          "Failed to fetch SEZ certificate request history",
          "SEZ_HISTORY_FAILED"
        )
      );
    }
  }
);

export const getSezCertificateFieldDifferences = createAsyncThunk(
  "sezDashboard/getSezCertificateFieldDifferences",
  async (sezCertReqId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<SezCertificateFieldDifferencesResult>(
        `/api/edo-rest-v2/v1/sez/getSezCertificateFieldDifferences`,
        {
          params: { sezCertReqId },
        }
      );

      return assertSezOk(response, "Failed to compare SEZ certificate fields");
    } catch (e) {
      console.error(e);
      return rejectWithValue(
        toSezApiError(
          e,
          "Failed to fetch certificate differences",
          "SEZ_FIELD_DIFF_FAILED"
        )
      );
    }
  }
);
